import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Key, Clock, Check, X, MessageSquare, AlertTriangle, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface KeyRequestWithGame {
	id: string;
	game_id: string;
	status: 'pending' | 'approved' | 'rejected';
	created_at: string;
	response_message: string | null;
	is_covered: boolean;
	message: string | null;
	is_activated?: boolean;
	game: {
		id: string;
		title: string;
		cover_image_url: string | null;
	};
	creator?: {
		id: string;
		display_name: string;
		avatar_url: string | null;
		is_verified?: boolean | null;
		has_reported?: boolean;
		report_details?: {
			total_reports: number;
			latest_report_date: string | null;
		};
		trust_score?: number;
	};
}

interface KeyRequestsListProps {
	requests: KeyRequestWithGame[];
	onViewTrackingInfo?: (gameId: string, gameKey: string) => void;
	onCancelRequest?: (requestId: string) => void;
	onAcceptRequest?: (requestId: string) => void;
	onRejectRequest?: (requestId: string) => void;
	onReportCreator?: (creatorId: string, isReporting: boolean) => void;
	onCreatorClick?: (creator: any) => void;
	showCancelButton?: boolean;
	userType?: 'creator' | 'developer' | null;
	hideTitle?: boolean;
	formatReportDate?: (date: string) => string;
	getTrustScoreColor?: (score: number) => string;
	getTrustScoreEmoji?: (score: number) => string;
	getTrustScoreLabel?: (score: number) => string;
	initialGameFilter?: string;
}

export function KeyRequestsList({

	requests,
	onViewTrackingInfo,
	onCancelRequest,
	onAcceptRequest,
	onRejectRequest,
	onReportCreator,
	onCreatorClick,
	showCancelButton = true,
	userType = null,
	hideTitle = false,
	formatReportDate,
	getTrustScoreColor,
	getTrustScoreEmoji,
	getTrustScoreLabel,
	initialGameFilter
}: KeyRequestsListProps) {
	const navigate = useNavigate();
	const [filter, setFilter] = useState<string>("all");
	const [gameFilter, setGameFilter] = useState<string>(initialGameFilter || "all");

	// Update game filter when initialGameFilter changes (from URL)
	useEffect(() => {
		if (initialGameFilter) {
			setGameFilter(initialGameFilter);
		}
	}, [initialGameFilter]);

	const [expandedId, setExpandedId] = useState<string | null>(null);
	const [reportDialogOpen, setReportDialogOpen] = useState(false);
	const [creatorToReport, setCreatorToReport] = useState<string | null>(null);

	// Get unique games for filter (only for developers)
	const uniqueGames = userType === 'developer'
		? Array.from(new Map(requests.map(req => [req.game.id, req.game])).values())
			.sort((a, b) => a.title.localeCompare(b.title))
		: [];

	const getStatusBadge = (status: string) => {
		switch (status) {
			case 'pending':
				return <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
			case 'approved':
				return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300"><Check className="w-3 h-3 mr-1" />Approved</Badge>;
			case 'rejected':
				return <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300"><X className="w-3 h-3 mr-1" />Rejected</Badge>;
			default:
				return <Badge variant="outline">{status}</Badge>;
		}
	};


	const getActivationBadge = (isActivated?: boolean) => {
		// Don't show badge if the value was never fetched (undefined for non-approved requests)
		if (isActivated === undefined) return null;

		return isActivated === true
			? <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300"><Check className="w-3 h-3 mr-1" />Activated</Badge>
			: <Badge variant="outline" className="bg-gray-100 text-gray-800 border-gray-300"><X className="w-3 h-3 mr-1" />Not activated</Badge>;
	};


	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	};

	const filteredRequests = requests.filter(request => {
		// Filter by status
		if (filter !== "all" && request.status !== filter) {
			return false;
		}

		// Filter by game (only for developers)
		if (userType === 'developer' && gameFilter !== "all" && request.game.id !== gameFilter) {
			return false;
		}

		return true;
	});

	// Group requests by game
	const requestsByGame = filteredRequests.reduce((acc, request) => {
		const gameId = request.game.id;
		if (!acc[gameId]) {
			acc[gameId] = {
				game: request.game,
				requests: []
			};
		}
		acc[gameId].requests.push(request);
		return acc;
	}, {} as Record<string, { game: KeyRequestWithGame['game']; requests: KeyRequestWithGame[] }>);

	const gameGroups = Object.values(requestsByGame).sort((a, b) =>
		a.game.title.localeCompare(b.game.title)
	);

	return (
		<div className="space-y-6">
			{!hideTitle && (
				<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
					<div className="flex-1 min-w-0">
						<h2 className="text-2xl font-bold mb-2">Key Requests</h2>
						<p className="text-muted-foreground text-sm">Manage all your key requests</p>
					</div>

					<div className="flex items-center gap-4 w-full sm:w-auto flex-wrap">
						<Select value={filter} onValueChange={setFilter}>
							<SelectTrigger className="w-full sm:w-40">
								<SelectValue placeholder="Filter by status" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All Requests</SelectItem>
								<SelectItem value="pending">Pending</SelectItem>
								<SelectItem value="approved">Approved</SelectItem>
								<SelectItem value="rejected">Rejected</SelectItem>
							</SelectContent>
						</Select>
						{userType === 'developer' && uniqueGames.length > 0 && (
							<Select value={gameFilter} onValueChange={setGameFilter}>
								<SelectTrigger className="w-full sm:w-40">
									<SelectValue placeholder="Filter by game" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">All Games</SelectItem>
									{uniqueGames.map((game) => (
										<SelectItem key={game.id} value={game.id}>
											{game.title}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						)}
					</div>
				</div>
			)}

			{filteredRequests.length === 0 ? (
				<Card className="p-8 text-center">
					<Key className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
					<h3 className="text-lg font-semibold mb-2">No requests found</h3>
					<p className="text-muted-foreground">
						{filter === "all"
							? "No key requests have been made yet."
							: `No ${filter} requests found.`
						}
					</p>
				</Card>
			) : userType === 'developer' ? (
				// New grouped design for developers
				<div className="space-y-8">
					{gameGroups.map((group) => {
						const stats = {
							total: group.requests.length,
							pending: group.requests.filter(r => r.status === 'pending').length,
							approved: group.requests.filter(r => r.status === 'approved').length,
							rejected: group.requests.filter(r => r.status === 'rejected').length
						};

						return (
							<Card key={group.game.id} className="rounded-custom-paddings px-perso-20">
								<div className="space-y-2">
									{/* Game Header */}
									<div className="flex flex-col sm:flex-row items-center gap-4">
										{group.game.cover_image_url && (
											<img
												src={group.game.cover_image_url}
												alt={group.game.title}
												className="h-20 sm:w-40 object-cover rounded-custom-paddings flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
												onClick={() => navigate(`/game/${group.game.id}`)}
											/>
										)}
										<div className="flex-1 min-w-0">
											<h3
												className="text-lg sm:text-xl font-bold mb-2 sm:mb-0 cursor-pointer hover:text-primary transition-colors"
												onClick={() => navigate(`/game/${group.game.id}`)}
											>
												{group.game.title}
											</h3>
											{/* Stats Summary */}
											<div className="flex flex-wrap gap-3 sm:gap-4 text-xs sm:text-sm">
												<span className="text-muted-foreground">
													<strong>{stats.total}</strong> request{stats.total !== 1 ? 's' : ''}
												</span>
												{stats.pending > 0 && (
													<span className="text-yellow-600">
														<strong>{stats.pending}</strong> pending
													</span>
												)}
												{stats.approved > 0 && (
													<span className="text-green-600">
														<strong>{stats.approved}</strong> accepted
													</span>
												)}
												{stats.rejected > 0 && (
													<span className="text-red-600">
														<strong>{stats.rejected}</strong> rejected
													</span>
												)}
											</div>
										</div>
									</div>

									{/* Requests List */}
									<div>
										{group.requests.map((request) => (
											<div key={request.id} className="flex flex-col sm:flex-row items-center gap-4 p-4 pb-4 pr-0 md:pr-6 mt-4 bg-muted/20 rounded-custom-paddings hover:bg-muted/30 transition-colors">
												{/* Creator Info */}
												{userType === 'developer' && request.creator && onCreatorClick ? (
													<div
														className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer ps-4 md:ps-0"
														onClick={() => onCreatorClick(request.creator)}
													>
														<Avatar className="w-10 h-10 flex-shrink-0">
															<AvatarImage src={request.creator.avatar_url || undefined} />
															<AvatarFallback>
																{request.creator.display_name?.charAt(0) || "C"}
															</AvatarFallback>
														</Avatar>
														<div className="min-w-0 flex-1">
															<div className="flex items-center gap-2 flex-wrap">
																<p className="font-medium truncate">{request.creator.display_name}</p>
																{request.creator.is_verified && (
																	<TooltipProvider>
																		<Tooltip>
																			<TooltipTrigger asChild>
																				<div className="flex-shrink-0">
																					<ShieldCheck className="w-4 h-4 text-blue-500" />
																				</div>
																			</TooltipTrigger>
																			<TooltipContent>
																				<p>Verified User</p>
																			</TooltipContent>
																		</Tooltip>
																	</TooltipProvider>
																)}
																{request.creator.trust_score !== undefined && getTrustScoreColor && getTrustScoreEmoji && getTrustScoreLabel && (
																	<Badge
																		variant="outline"
																		className={`text-xs ${getTrustScoreColor(request.creator.trust_score)} border-current flex-shrink-0`}
																	>
																		{getTrustScoreEmoji(request.creator.trust_score)} {getTrustScoreLabel(request.creator.trust_score)}
																	</Badge>
																)}
															</div>
															{request.creator.has_reported && formatReportDate && (
																<div className="text-xs text-muted-foreground mt-1">
																	⚠️ Reported: {request.creator.report_details?.latest_report_date
																		? formatReportDate(request.creator.report_details.latest_report_date)
																		: 'Unknown date'
																	}
																</div>
															)}
														</div>
													</div>
												) : null}

												{/* Message */}
												{request.message && (
													<div className="text-center w-fit">
														<TooltipProvider>
															<Tooltip>
																<TooltipTrigger asChild>
																	<div className="flex gap-1 text-sm text-muted-foreground bg-muted/50 p-3 rounded-custom-paddings cursor-help">
																		<MessageSquare className="w-4 h-4 inline mt-0.5" />
																		<span className="block sm:hidden">{request.message}</span>
																		<span className="block hidden sm:block">Message</span>
																	</div>
																</TooltipTrigger>
																<TooltipContent className="max-w-xs">
																	<p>{request.message} </p>
																</TooltipContent>
															</Tooltip>
														</TooltipProvider>

													</div>
												)}

												{/* Status and Actions */}
												<div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto sm:flex-shrink-0">
													<div className="flex flex-col sm:items-end gap-2">
														<div className="flex flex-wrap gap-2">
															{getStatusBadge(request.status)}
															{request.status === 'approved' && getActivationBadge(request.is_activated)}
														</div>
														<span className="text-xs text-muted-foreground">
															{formatDate(request.created_at)}
														</span>
													</div>

													{/* Actions */}
													{userType === 'developer' && (
														<div className="flex flex-row sm:flex-col gap-2 w-full sm:w-auto pb-4 md:pb-0">
															{request.status === 'approved' && request.response_message && onViewTrackingInfo && (
																<Button
																	size="sm"
																	variant="outline"
																	onClick={(e) => {
																		e.stopPropagation();
																		const gameKey = request.response_message?.replace('Key assigned: ', '') || '';
																		onViewTrackingInfo(request.game.id, gameKey);
																	}}
																	className="flex-1 sm:flex-none"
																>
																	<Key className="w-4 h-4 mr-2" />
																	Key
																</Button>
															)}
															{onReportCreator && !request.creator?.has_reported && (
																<Button
																	size="sm"
																	variant="outline"
																	onClick={(e) => {
																		e.stopPropagation();
																		setCreatorToReport(request.creator!.id);
																		setReportDialogOpen(true);
																	}}
																	className="text-xs flex-1 sm:flex-none"
																>
																	<AlertTriangle className="w-3 h-3 mr-1" />
																	Report
																</Button>
															)}
															{request.status === 'pending' && onAcceptRequest && onRejectRequest && (
																<>
																	<Button
																		size="sm"
																		onClick={(e) => {
																			e.stopPropagation();
																			onAcceptRequest(request.id);
																		}}
																		className="bg-green-600 hover:bg-green-700 text-white flex-1 sm:flex-none"
																	>
																		<Check className="w-4 h-4 mr-1" />
																		Accept
																	</Button>
																	<Button
																		size="sm"
																		variant="outline"
																		onClick={(e) => {
																			e.stopPropagation();
																			onRejectRequest(request.id);
																		}}
																		className="bg-red-600 text-white hover:bg-red-500 flex-1 sm:flex-none p-0 h-7 text-xs"
																	>
																		<X className="w-4 h-4 mr-1" />
																		Decline
																	</Button>
																</>
															)}
														</div>
													)}
												</div>
											</div>
										))}
									</div>
								</div>
							</Card>
						);
					})}
				</div>
			) : (
				// Original card design for creators and other users
				<div className="grid md:grid-cols-2 gap-3 grid-cols-1">
					{filteredRequests.map((request) => (
						<Card key={request.id} className="p-6 rounded-custom-paddings">
							<div className="space-y-4 ">
								<div className="flex flex-col sm:flex-row gap-4">
									{request.game.cover_image_url && (
										<img
											src={request.game.cover_image_url}
											alt={request.game.title}
											className="w-full sm:w-32 h-32 object-cover rounded-custom-paddings flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
											onClick={() => navigate(`/game/${request.game.id}`)}
										/>
									)}
									<div className="flex-1 min-w-0">
										<h3
											className="font-semibold text-lg mb-2 cursor-pointer hover:text-primary transition-colors"
											onClick={() => navigate(`/game/${request.game.id}`)}
										>
											{request.game.title}
										</h3>
										<div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
											<div className="flex flex-wrap gap-2">
												{getStatusBadge(request.status)}
												{request.status === 'approved' && getActivationBadge(request.is_activated)}
												{request.is_covered && (
													<Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
														<Check className="w-3 h-3 mr-1" />
														Covered
													</Badge>
												)}
											</div>
											<span className="text-sm text-muted-foreground">
												{formatDate(request.created_at)}
											</span>
										</div>
										{request.message && (
											<p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-custom-paddings mr-4 w-fit px-perso-20">
												<MessageSquare className="w-4 h-4 inline mr-1" />
												{request.message}
											</p>
										)}
										{request.status === 'approved' && request.response_message && onViewTrackingInfo && (
											<div className="mt-3">
												<Button
													size="sm"
													variant="outline"
													onClick={(e) => {
														e.stopPropagation();
														const gameKey = request.response_message?.replace('Key assigned: ', '') || '';
														onViewTrackingInfo(request.game.id, gameKey);
													}}
												>
													<Key className="w-4 h-4 mr-2" />
													View Game Key & Tracking Info
												</Button>
											</div>
										)}
									</div>
								</div>

								{/* Cancel button - Only show for creators on pending requests */}
								{userType === 'creator' && request.status === 'pending' && showCancelButton && onCancelRequest && (
									<div className="flex justify-end">
										<Button
											size="sm"
											variant="outline"
											onClick={(e) => {
												e.stopPropagation();
												onCancelRequest(request.id);
											}}
											className="border-orange-300 text-orange-600 hover:bg-orange-50"
										>
											<X className="w-4 h-4 mr-1" />
											Cancel Request
										</Button>
									</div>
								)}
							</div>
						</Card>
					))}
				</div>
			)}

			<AlertDialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Report Creator</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to report this creator? This action will notify the administrators to review this account.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => {
							setReportDialogOpen(false);
							setCreatorToReport(null);
						}}>Cancel</AlertDialogCancel>
						<AlertDialogAction onClick={() => {
							if (creatorToReport && onReportCreator) {
								onReportCreator(creatorToReport, true);
								setReportDialogOpen(false);
								setCreatorToReport(null);
							}
						}} className="bg-red-600 hover:bg-red-700 text-white">
							Report
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}

